import { useEffect, useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useParams } from "react-router-dom"
import classNames from "classnames"
import { t } from "i18next"
import { FieldOption } from "../../types/FieldOption"
import { FieldProps } from "../../types/FieldProps"
import { FormQuestion as FormQuestionType } from "../../types/FormQuestion"
import { InputName } from "../../types/InputName"
import { getDynamicFieldSchema } from "../../utils/getDynamicFieldSchema"
import { CheckboxGroup } from "../CheckboxGroup"
import { Select } from "../Select"
import { RadioGroup } from "../RadioGroup"
import { TextArea } from "../TextArea"
import { TextField } from "../TextField"
import { SubHeader } from "../SubHeader"
import { FormSection } from "../../types/FormSection"
import { Question } from "../../types/SectionState"
import { useInitialFormStateByProvince } from "../../hooks/useInitialFormState"
import { useSectionState } from "../../context/SectionContextProvider"

export interface Props {
  question: FormQuestionType
  sectionName: FormSection["section_name"]
  onValidate: (question: Question) => void
  onBlur: (question: Question) => void
}

type InputMap = {
  [key in InputName]: (props: FieldProps) => JSX.Element
}

const getFieldOptions = (
  options: FormQuestionType["options"],
): FieldOption[] => {
  return options?.map(({ key, label }) => ({ label, value: String(key) })) || []
}

const isArrayField = (input_name: InputName) => {
  return (
    input_name === "checkbox-multiselect" || input_name === "chips-multiselect"
  )
}

const isDateField = (validationType: string) => {
  return validationType === "age"
}

const getFieldType = (question: FormQuestionType) => {
  if (isArrayField(question.input_name)) return "array"
  else if (question.validation_type && isDateField(question.validation_type)) {
    return "date"
  }
  return "string"
}

const getQuestionSchema = (
  question: FormQuestionType,
  values: { [key: string]: any },
) => {
  const fieldType = getFieldType(question)
  return getDynamicFieldSchema({
    fieldName: question.mapped_field,
    fieldType,
    isOptional: question.optional || question.ignore_empty,
    messages: {
      required: question.missing_message || "",
      validation: question.error_message || "",
    },
    method: question.validation_type,
    pattern: question.validation_pattern,
    mustMatchFieldName: question.must_match_field,
    values,
    minimum_multiselect_selections:
      question.minimum_multiselect_selections || 1,
  })
}

const getDisplayValue = (fieldValue: any, question: FormQuestionType) => {
  const options = getFieldOptions(question.options)
  const getLabel = ({ label }: FieldOption) => label
  if (Array.isArray(fieldValue)) {
    return options
      .filter(({ value }: FieldOption) => fieldValue?.includes(value))
      .map(getLabel)
      .join("\r\n")
  } else if (
    question.input_name === "select" ||
    question.input_name === "radio-multiselect"
  ) {
    return options
      .filter(({ value }) => value === fieldValue)
      .map(getLabel)
      .join(", ")
  } else {
    return question.text_transform === "uppercase"
      ? String(fieldValue).toUpperCase()
      : fieldValue
  }
}

export function FormQuestion({
  question,
  sectionName,
  onValidate,
  onBlur,
}: Props) {
  const { control, trigger, getValues, resetField } = useFormContext()
  const [hasSelectedSelfDefine, setHasSelectedSelfDefine] = useState(false)
  const { province } = useParams<{ province: string }>()
  const { initialFormState } = useInitialFormStateByProvince(
    province ?? "ontario",
  )
  const sectionState = useSectionState()
  const hasErrors = sectionState[sectionName]?.errors
  const {
    mapped_field,
    helper_text,
    prompt,
    input_name,
    input_mask,
    options: formQuestionOptions,
    mask_placeholder,
    value: defaultValue,
    text_transform,
    max_characters,
    placeholder_message,
    self_define_option_id,
    nested_question,
    optional,
  } = question

  const options = useMemo(
    () => getFieldOptions(formQuestionOptions),
    [formQuestionOptions],
  )

  useEffect(() => {
    if (initialFormState?.[mapped_field]) {
      trigger(mapped_field)
    }
  }, [initialFormState, mapped_field, trigger])

  useEffect(() => {
    if (nested_question && initialFormState?.[nested_question.mapped_field]) {
      trigger(nested_question.mapped_field)
    }
  }, [initialFormState, trigger, nested_question])

  const inputMap: InputMap = useMemo(
    () => ({
      text: (props) => (
        <TextField
          {...props}
          optional={optional}
          mask={input_mask || ""}
          maskPlaceholder={mask_placeholder || ""}
          textTransform={text_transform}
          subText={optional ? t("client.form.properties.optional") : undefined}
          maximumCharacters={max_characters || undefined}
        />
      ),
      "read-only": (props) => (
        <TextField
          {...props}
          optional={optional}
          inputProps={{ disabled: true }}
        />
      ),
      "text-area": (props) => (
        <TextArea
          {...props}
          optional={optional}
          maximumCharacters={max_characters || undefined}
        />
      ),
      "checkbox-multiselect": (props) => (
        <CheckboxGroup {...props} optional={optional} options={options || []} />
      ),
      "radio-multiselect": (props) => (
        <RadioGroup {...props} optional={optional} options={options || []} />
      ),
      select: (props) => (
        <Select options={options || []} optional={optional} {...props} />
      ),
      "chips-multiselect": (props) => (
        <Select
          optional={optional}
          options={options || []}
          {...props}
          isMulti
        />
      ),
      label: (props: FieldProps) => <SubHeader {...props} />,
    }),
    [
      options,
      input_mask,
      mask_placeholder,
      text_transform,
      max_characters,
      optional,
    ],
  )

  const Field = useMemo(() => inputMap[input_name], [input_name, inputMap])

  const handleSelfDefine = (value: any) => {
    const hasSelectedSelfDefine = Array.isArray(value)
      ? value.some((value: string) => value === String(self_define_option_id))
      : false
    if (!hasSelectedSelfDefine && nested_question) {
      resetField(nested_question.mapped_field, { defaultValue: "" })
    }
    setHasSelectedSelfDefine(hasSelectedSelfDefine)
  }

  const handleValidation = async (value: any, question: FormQuestionType) => {
    const sectionQuestionState = sectionState[sectionName]?.questions.find(
      ({ fieldName }) => fieldName === question.mapped_field,
    )
    const hasNotBeenTouched = !sectionQuestionState?.touched

    if (defaultValue && hasNotBeenTouched) {
      onValidate({
        fieldName: question.mapped_field,
        sectionName,
        touched: false,
        displayValue: getDisplayValue(value, question),
      })
      return
    }

    if (Array.isArray(value)) {
      handleSelfDefine(value)
    }

    if (question.mask_placeholder) {
      const regex = new RegExp(`[${question.mask_placeholder}/s/]/g`)
      const maskedFieldHasValues = value?.replace(regex, "").length
      if (!maskedFieldHasValues && question.optional) {
        return true
      }
    }

    try {
      await getQuestionSchema(question, getValues()).validate({
        [question.mapped_field]: value,
      })
      onValidate({
        fieldName: question.mapped_field,
        sectionName,
        touched: Boolean(value),
        displayValue: getDisplayValue(value, question),
      })
    } catch (err: any) {
      onValidate({
        error: Boolean(err),
        fieldName: question.mapped_field,
        sectionName,
        touched: Boolean(value),
        displayValue: getDisplayValue(value, question),
      })
      return err.message
    }
    return true
  }

  const handleBlur = (e: any, question: FormQuestionType) => {
    const { name: fieldName, value } = e.target
    trigger(fieldName)
    if (question.mask_placeholder && question.input_mask) {
      const placeholder = question.input_mask.replace(
        /\S/g,
        question.mask_placeholder,
      )
      if (value === placeholder) {
        resetField(fieldName, { defaultValue: "" })
      }
    }
    if (hasErrors && question.must_match_field) {
      const fieldNames = sectionState[sectionName].questions.map(
        ({ fieldName }) => fieldName,
      )
      trigger(fieldNames)
    }
    onBlur({
      fieldName,
      sectionName,
      touched: Boolean(value),
      displayValue: getDisplayValue(value, question),
    })
  }

  return (
    <>
      <Field
        placeholder={placeholder_message || ""}
        name={mapped_field}
        defaultValue={defaultValue || undefined}
        helperText={helper_text || ""}
        control={control}
        rules={{
          validate: (value) => handleValidation(value, question),
          onChange: (e) => handleSelfDefine(e.target.value),
          onBlur: (e) => handleBlur(e, question),
        }}
        label={prompt}
      />
      {nested_question ? (
        <TextField
          className={classNames("pt-3", {
            hidden: !hasSelectedSelfDefine,
          })}
          label={nested_question.prompt}
          name={nested_question.mapped_field}
          control={control}
          rules={{
            validate: (value) => handleValidation(value, nested_question),
            onBlur: (e) => handleBlur(e, nested_question),
          }}
        />
      ) : null}
    </>
  )
}
